<script setup lang="ts">
import type { PropertyPricesProps } from './types';
import type { ContactFormSectionExpose } from '~/components/ContactFormSection';

import FavoriteButtonSquare from '@/components/FavoriteButtonSquare/FavoriteButtonSquare.vue';
import { deepMergeLocales } from '@/utils/deepMerge';
import { createLocaleTranslator } from '@/utils/localeMessages';
import { useEvents } from '~/composables/events/useEvents';
import { useContactForm } from '~/composables/useContactForm';
import { usePropertyContext } from '~/composables/usePropertyContext';
import { useToggleFavoriteSync } from '~/composables/useToggleFavoriteSync';
import { EventBusKeys } from '~/core/helpers/symbols';
import { MixpanelProps } from '~/core/service/mixpanel';
import { hasShortsVideo } from '~/utils';
import { getAgentFirstName, getAgentName } from '~~/utils';

import contactFormLocale from '~/components/ContactFormModal/ContactFormModal.locale.json';
import { ContactFormSection } from '~/components/ContactFormSection';
import { PrivacyTermsStatement } from '~/components/PrivacyTermsStatement';

import propertyPricesLocale from './PropertyPrices.locale.json';
import PropertyPricesSkeleton from './PropertyPricesSkeleton.vue';

withDefaults(defineProps<PropertyPricesProps>(), {
	priceClasses: '',
	condoSlug: '',
	isExclusive: false
});

const emit = defineEmits<{
	(e: 'openContactModal'): void;
}>();

const { property, isLoading, sectionName, isContactModalOpen } =
	usePropertyContext();

// Use shared form state
const {
	name,
	phone,
	email,
	userMessage,
	errors,
	nameAttrs,
	phoneAttrs,
	emailAttrs,
	userMessageAttrs,
	validate,
	meta,
	setFieldValue,
	isPhoneDisabled
} = useContactForm();

// Combine locale messages with deep merge
const combinedMessages = computed(() => {
	return deepMergeLocales(contactFormLocale, propertyPricesLocale);
});

const strings = combinedMessages.value.pt as Record<string, any>;

const { translateByContext } = createLocaleTranslator(strings, 'property');

const { loggedIn } = useUserSession();

const agentName = computed(() => getAgentName(property.value?.agent));
const firstName = computed(() => getAgentFirstName(property.value?.agent));

const {
	trackFormPropertyClicked
	// trackSharePropertyClicked
} = useEvents();

function openContactModal() {
	if (!property.value?.agent || !property.value?.commercialId) return;

	emit('openContactModal');

	trackFormPropertyClicked({
		[MixpanelProps.USER_STATUS]: loggedIn.value ? 'logged in' : 'logged out',
		[MixpanelProps.AGENT_NAME]: property.value.agent.name,
		[MixpanelProps.COMPANY_NAME]: property.value.company?.name || null,
		[MixpanelProps.COMMERCIAL_ID]: property.value.commercialId,
		[MixpanelProps.CTA_TEXT]: strings.broker.button,
		[MixpanelProps.SECTION_NAME]:
			sectionName?.value || 'card (property details)',
		[MixpanelProps.CURRENT_PAGE]:
			sectionName?.value === 'card (property details)'
				? 'Detalhe do Imóvel'
				: 'PDP Form'
	});

	if (isContactModalOpen?.value !== undefined) {
		isContactModalOpen.value = true;
	}
}

const getImageSrc = useImage();

const brokerSrc = computed(() =>
	getImageSrc(property.value?.agent?.profilePicture?.croppedUrl || '', {
		format: 'webp',
		width: 100
	})
);

// Form UI state
const shouldExpandContainer = computed(() => {
	return !!(name.value?.trim() && phone.value?.trim());
});

// ContactFormSection component reference
const contactFormSection = useTemplateRef<ContactFormSectionExpose | null>(
	'contactFormSection'
);

// Function to open modal directly in OTP step
async function openContactModalAtOtp() {
	await validate();

	const isFormValid = meta.value.valid;

	if (!isFormValid) return;

	if (!property.value?.agent || !property.value?.commercialId) return;

	// Check if form is valid before proceeding
	if (!isTalkToBrokerButtonEnabled.value) return;

	// Use event-bus to open modal in OTP step
	useEventBus<void>(EventBusKeys.openContactModalAtOtp).emit();

	trackFormPropertyClicked({
		[MixpanelProps.USER_STATUS]: loggedIn.value ? 'logged in' : 'logged out',
		[MixpanelProps.AGENT_NAME]: property.value.agent.name,
		[MixpanelProps.COMPANY_NAME]: property.value.company?.name || null,
		[MixpanelProps.COMMERCIAL_ID]: property.value.commercialId,
		[MixpanelProps.CTA_TEXT]: 'Falar com o corretor',
		[MixpanelProps.SECTION_NAME]:
			sectionName?.value || 'card (property details)',
		[MixpanelProps.CURRENT_PAGE]: 'Detalhe do Imóvel'
	});
}

// Function to submit form (opens normal modal)
function handleFormSubmit() {
	openContactModal();
}

// Function to handle expandable container toggle
function handleToggleExpanded(_isExpanded: boolean) {
	// Log event if needed
}

// Function to handle form data updates
function handleFormDataUpdate(
	updatedData: Partial<{
		name: string;
		phone: string;
		email: string;
		userMessage: string;
	}>
) {
	// Only update fields that are provided (not undefined) and are valid strings
	if ('name' in updatedData) {
		const value = updatedData.name?.trim() ?? '';
		setFieldValue('name', value);
	}
	if ('phone' in updatedData) {
		const value = updatedData.phone?.trim() ?? '';
		setFieldValue('phone', value);
	}
	if ('email' in updatedData) {
		const value = updatedData.email?.trim() ?? '';
		setFieldValue('email', value);
	}
	if ('userMessage' in updatedData) {
		const value = updatedData.userMessage?.trim() ?? '';
		setFieldValue('userMessage', value);
	}
}

// Set initial form message
const initialUserMessage = computed<string>(() => {
	if (!property.value) return '';

	return translateByContext('form.userMessage.initialValue', {
		propertyCommercialId: property.value.commercialId,
		propertyRegion: property.value.region,
		propertyType: property.value.propertyType?.name?.toLowerCase() || ''
	});
});

// Set initial message when property loads
watch(
	initialUserMessage,
	message => {
		if (message && !userMessage.value) {
			setFieldValue('userMessage', message);
		}
	},
	{ immediate: true }
);

// Computed to determine if "Talk to broker" button should be enabled
const isTalkToBrokerButtonEnabled = computed(() => {
	// If form is valid, always enable
	if (meta.value.valid) return true;

	// If user is logged and basic data is filled
	if (loggedIn.value && name.value && phone.value) {
		return true;
	}

	return false;
});

const stickyPulseTriggered = ref(false);
const isStickyPulseEnabled = ref(false);

const shouldShowStickyPulse = inject<Ref<boolean>>('shouldShowStickyPulse');

watch(
	() => shouldShowStickyPulse?.value,
	isSticky => {
		if (isSticky && !stickyPulseTriggered.value) {
			isStickyPulseEnabled.value = true;
			stickyPulseTriggered.value = true;
		} else if (!isSticky) {
			isStickyPulseEnabled.value = false;
		}
	},
	{ immediate: true }
);

const propertyIdRef = computed(() => property.value?.id);
const commercialIdRef = computed(() => property.value?.commercialId);

const route = useRoute();

const eventData = computed(() => ({
	currentPage: route.fullPath,
	tagExclusive: property.value?.isExclusive,
	companyName: property.value?.company?.name,
	agentName: property.value?.agent?.name,
	clickProperty: true,
	videoShorts: hasShortsVideo(property.value)
}));

const initialFavoriteState = computed(
	() => property.value?.inUserWishlist ?? false
);

// useToggleFavoriteSync is synchronous - no await needed for SSR performance
const {
	isFavorite,
	isValidData,
	handleToggleFavorite: toggleFavorite
} = useToggleFavoriteSync(
	propertyIdRef,
	commercialIdRef,
	eventData,
	'card (property details)',
	initialFavoriteState
);
</script>

<template>
	<PropertyPricesSkeleton v-if="isLoading" :is-loading="true" />

	<div v-else class="z-20 size-full bg-secondary px-4 py-5 md:p-5">
		<!-- Integrated Contact Form -->
		<ContactFormSection
			ref="contactFormSection"
			:form-data="{
				name,
				phone,
				email,
				userMessage
			}"
			:validation="{
				errors,
				isValid: meta.valid,
				isPhoneDisabled: !!isPhoneDisabled
			}"
			:form-attributes="{
				nameAttrs,
				phoneAttrs,
				emailAttrs,
				userMessageAttrs
			}"
			:broker-info="{
				broker: property?.agent,
				name: agentName,
				avatarSrc: brokerSrc,
				message: translateByContext('brokerMessage'),
				atText: translateByContext('brokerAt', {
					brokerCompany: property?.company?.name
				}),
				companyName: property?.company?.name,
				companyLogo: property?.company?.smallLogo
			}"
			:text-content="{
				expandText: strings.showMoreInfo,
				collapseText: strings.showLessInfo,
				namePlaceholder: translateByContext('form.name.placeholder'),
				emailPlaceholder: translateByContext('form.email.placeholder'),
				userMessagePlaceholder: translateByContext(
					'form.userMessage.placeholder'
				)
			}"
			:app-state="{
				isPropertyContext: true,
				isUserLoggedIn: loggedIn,
				shouldExpandContainer
			}"
			class="px-0!"
			@update:form-data="handleFormDataUpdate"
			@submit="handleFormSubmit"
			@toggle-expanded="handleToggleExpanded"
		/>

		<!-- Button to talk to broker (opens modal in OTP step) -->
		<Button
			size="md"
			class="mt-6 flex w-full items-center gap-3 text-base font-medium"
			@click="openContactModalAtOtp"
		>
			<IconMaterialSymbolsLightChatBubbleOutlineSharp
				class="size-6 text-beige"
			/>
			{{ strings.broker.button }} {{ firstName }}
		</Button>
		<PrivacyTermsStatement :messages="combinedMessages" />

		<div class="mt-4 border-t border-white-dark-3"></div>

		<!-- Favorite button wrapped in ClientOnly to prevent hydration mismatch -->
		<!-- The wishlist state differs between server (empty) and client (populated) -->
		<div v-if="isValidData" class="flex w-full items-center gap-3 pt-4">
			<ClientOnly>
				<template #fallback>
					<FavoriteButtonSquare
						class="w-full min-w-0 transition-opacity duration-500"
						:is-selected="false"
						:is-enabled-for-pulse="false"
						:show-label="true"
						:show-label-on-mobile="true"
						fade-animation="enabled"
						aria-label="Adicionar à minha coleção"
					/>
				</template>
				<FavoriteButtonSquare
					class="w-full min-w-0 transition-opacity duration-500"
					:is-selected="isFavorite"
					:is-enabled-for-pulse="isStickyPulseEnabled"
					:show-label="true"
					:show-label-on-mobile="true"
					fade-animation="enabled"
					:aria-label="
						isFavorite
							? 'Remover da minha coleção'
							: 'Adicionar à minha coleção'
					"
					@toggle="toggleFavorite"
				/>
			</ClientOnly>
		</div>
	</div>
</template>
